import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { makeStyles } from '@material-ui/core/styles';

import { makeHeadData, makeGraph, twoDigits } from 'lib/commonLib';
import { managerDetailTableData, managerDetailGraphData } from 'lib/api/media';

import Modal from 'components/organisms/NewModal';
import BasicTable from 'components/molecules/MbBasicTable';
import MultiplexChart from 'components/organisms/Chart/MultiplexChart';
import SnackbarWrap from 'components/organisms/Snackbar/SnackbarWrap';
import ContentsWrap from 'components/molecules/ContentsWrap';

const useStyles = makeStyles((theme) => ({}));

const monthLabels = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
  'JAN',
];
const customized = {};

const GraphModal = (props) => {
  const { handleModalClose, params, modal } = props;
  const { t } = useTranslation();
  const classes = useStyles();

  const { month } = params;

  const [tableList, setTableList] = useState([]);
  const [curGraphComponent, setCurGraphComponent] = useState(false);
  const initSnackbar = {
    open: false,
    variant: '',
    message: '',
  };
  const [snackbar, setSnackbar] = useState(initSnackbar);
  let initLoading = {
    graph: true,
    table: true,
  };
  const [loading, setLoading] = useState(initLoading);
  const [tableHead, setTableHead] = useState({
    headList: [
      {
        id: 'LABEL',
        label: t('월간 통계데이터'),
        orderFlag: false,
        type: 'string',
        format: '@',
      },
    ],
    sort: 'desc',
    order: 'STATS_DTTM',
  });

  const label =
    modal.params.name +
    t('님의') +
    ' ' +
    t('까지의 통계 데이터입니다.', {
      date: t('term', {
        startDate:
          moment().add(-1, 'year').format('YYYY') +
          t('년') +
          ' ' +
          month +
          t('월'),
        endDate: moment().format('YYYY') + t('년') + ' ' + month + t('월'),
      }),
    });

  useEffect(() => {
    let curMoment,
      curMonth,
      newTableHead = [...tableHead.headList];
    for (let i = 11; i >= 0; i--) {
      curMoment = moment().add(i * -1, 'month');
      curMonth = {
        id: monthLabels[12 - i],
        label: curMoment.format('M') + t('월'),
        orderFlag: false,
        displayFlag: true,
      };
      newTableHead.push(curMonth);
    }
    setTableHead({
      ...tableHead,
      headList: newTableHead,
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    getGraphData();
    // eslint-disable-next-line
  }, [modal.params]);

  const getData = () => {
    const detailParams = { contactNo: modal.params.contactNo };
    managerDetailTableData(detailParams)
      .then((response) => {
        const { data } = response.data;

        setTableList(data);
      })
      .catch((error) => {
        setSnackbar({
          ...snackbar,
          open: true,
          variant: 'error',
          message: error,
        });
      })
      .finally(() => {
        setLoading({
          table: false,
          graph: false,
        });
      });
  };

  const getGraphData = () => {
    const detailParams = { contactNo: modal.params.contactNo };
    managerDetailGraphData(detailParams)
      .then((response) => {
        const primitiveData = response.data.data;
        if (typeof primitiveData !== 'undefined') {
          const data = { bar: ['GOAL_ADVRTS_AMT', 'ADVRTS_AMT'] };
          const customize = {};
          const referenceLine = {
            ADVRTS_AMT: {
              type: 'average',
              label: t('지출금액'),
              visibleValue: true,
            },
          };
          const {
            graphData,
            dataKeys,
            customizeData,
            keyLabel,
            stackId,
            referenceData,
          } = makeGraph({ data, customize, primitiveData, referenceLine });
          const yAxisData = {
            left: { ADVRTS_AMT: true },
            right: {},
          };

          const result = (
            <MultiplexChart
              width="100%"
              xAxisTick={xAxisTick}
              makeLabel={makeLabel(false)}
              data={graphData}
              dataKeys={dataKeys}
              customizeData={customizeData}
              keyLabel={keyLabel}
              stackId={stackId.length !== 0 ? stackId.join('$') : null}
              yAxisData={yAxisData}
              legend
              isPercent={false}
              key={t('목표지출금액')}
              referenceLine={referenceData}
            />
          );

          setCurGraphComponent(result);
        }
      })
      .catch((error) => {
        setSnackbar({
          ...snackbar,
          open: true,
          variant: 'error',
          message: error,
        });
      })
      .finally(() => {
        getData();
      });
  };

  // 월 일 or 일 반환11
  const makeLabel = (onlyDay) => (originalDate) => {
    const dateObj = moment(originalDate, 'YYYYMM');
    const month = twoDigits(dateObj.month() + 1);
    const year = twoDigits(dateObj.year());

    return onlyDay ? (
      `${t(month + '월')}`
    ) : (
      <span>
        {`${t(year + '년')}`}
        {`${t(month + '월')}`}
      </span>
    );
  };

  const xAxisTick = (tickProps) => {
    const { x, y, payload } = tickProps;
    const { value } = payload;

    const dayStr = makeLabel(true)(value);

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={-15} y={-8} dy={20} textAnchor="center">
          {dayStr}
        </text>
      </g>
    );
  };

  /**
   * 스낵바 닫는 함수
   * 연속 클릭이 들어왔을 때는 기존 스낵바를 유지
   * @param event
   * @param reason
   */
  const handleSnackBarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({
      ...snackbar,
      open: false,
      message: '',
    });
  };

  return (
    <>
      <Modal
        open={modal.open}
        headerTitle={label}
        handleClose={handleModalClose}
        confirmOnClick={handleModalClose}
      >
        <ContentsWrap loading={loading.graph} contentsCount={1}>
          {curGraphComponent}
        </ContentsWrap>
        <BasicTable
          sort={tableHead.sort}
          order={tableHead.order}
          list={tableList}
          headCells={makeHeadData(tableHead.headList)}
          loading={loading.table}
          className={`${classes.issueTable}`}
          customized={customized}
        />
        <SnackbarWrap
          open={snackbar.open}
          variant={snackbar.variant}
          message={snackbar.message}
          onClose={handleSnackBarClose}
        />
      </Modal>
    </>
  );
};
export default GraphModal;
